import React, { useState } from 'react';
import { Button, ButtonGroup, CircularProgress, Grid2, Tooltip } from '@mui/material';
import { useGetBookings, getBookings } from '@openapi/bookings/bookings';
import useNotify from '../../hooks/useNotify';
import { BOOKING_STATUSES, TABLE_ROW_COUNT_DEFAULT } from '../../helpers/constants';
import PageWithTitle from '../../components/atom/PageWithTitle';
import BookingsTable from './components/BookingsTable';
import BookingsFilter from './components/BookingsFilter';
import { useTranslation } from 'react-i18next';
import AddIcon from '@mui/icons-material/Add';
import { MODAL_TYPE, useModals } from '../../hooks/useModal';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import { collectAllData, exportToXlsx } from '../../helpers/excelExportHelpers';
import { useCapabilities } from '../../hooks/useCapabilities';

const INITIAL_FILTERS = {
    search: '',
    bookingId: undefined,
    confirmationCode: undefined,
    timeslotId: null,
    showId: null,
    statuses: [],
    ticketTypeId: null,
    commissionGroupId: null,
    paymentMethodSlug: null,
};

const BookingsPage = () => {
    const { t } = useTranslation();
    const { showModal } = useModals();
    const { notifyError, notifySuccess } = useNotify();
    const { can, CAPABILITIES } = useCapabilities();
    const canManageBookings = can(CAPABILITIES.BOOKINGS_MANAGE);
    const canViewStats = can(CAPABILITIES.BOOKINGS_SHOW_STATS);

    const [filters, setFilters] = useState(INITIAL_FILTERS);
    const [params, setParams] = useState({
        page: 0,
        pageSize: TABLE_ROW_COUNT_DEFAULT,
        sort: 'created_at',
        sortDir: 'DESC',
    });

    const [generatingXlsx, setGeneratingXlsx] = useState(false);
    const [progress, setProgress] = useState(0);

    const {
        data: bookingsData,
        isLoading: loading,
        refetch,
    } = useGetBookings(
        {
            page: params.page,
            pageSize: params.pageSize,
            sort: params.sort,
            sortDir: params.sortDir,
            optionalId: filters.timeslotId || undefined,
            showId: filters.showId || undefined,
            search: filters.search || undefined,
            bookingId: filters.bookingId || undefined,
            confirmationCode: filters.confirmationCode || undefined,
            statusFilter: filters.statuses.length > 0 ? filters.statuses.join(',') : undefined,
            includeSummary: canViewStats,
            ticketTypeId: filters.ticketTypeId || undefined,
            commissionGroupId: filters.commissionGroupId || undefined,
            paymentMethodSlug: filters.paymentMethodSlug || undefined,
        },
        {
            query: {
                onError: notifyError,
            },
        },
    );

    const clearFilters = () => {
        setFilters(INITIAL_FILTERS);
        setParams((prev) => ({ ...prev, page: 0 }));
    };

    const handleSorting = (sortKey) => {
        setParams((prev) => ({
            ...prev,
            sort: sortKey,
            sortDir: prev.sort === sortKey && prev.sortDir === 'ASC' ? 'DESC' : 'ASC',
            page: 0,
        }));
    };

    return (
        <PageWithTitle
            title={t('bookings.title')}
            headline={[
                <Grid2 key={'add-booking'}>
                    <ButtonGroup variant="contained">
                        {canManageBookings && (
                            <Tooltip title={t('bookings.newBooking')}>
                                <Button
                                    variant="contained"
                                    endIcon={<AddIcon />}
                                    onClick={() => {
                                        showModal(MODAL_TYPE.ADD_BOOKING, {});
                                    }}
                                    sx={{ px: 5 }}
                                >
                                    {t('bookings.create')}
                                </Button>
                            </Tooltip>
                        )}
                        <Tooltip title="Export">
                            <Button
                                fullWidth
                                disabled={generatingXlsx}
                                onClick={async () => {
                                    setGeneratingXlsx(true);
                                    let listToExport = [];
                                    try {
                                        listToExport = await collectAllData({
                                            data: bookingsData,
                                            apiFunction: (page, pageSize) => {
                                                return getBookings({
                                                    optionalId: filters.timeslotId || undefined,
                                                    showId: filters.showId || undefined,
                                                    statusFilter:
                                                        filters.statuses.length > 0
                                                            ? filters.statuses.join(',')
                                                            : undefined,
                                                    search: filters.search || undefined,
                                                    ticketTypeId: filters.ticketTypeId || undefined,
                                                    commissionGroupId:
                                                        filters.commissionGroupId || undefined,
                                                    paymentMethodSlug:
                                                        filters.paymentMethodSlug || undefined,
                                                    page,
                                                    pageSize,
                                                });
                                            },
                                            setProgress,
                                            responseData: 'bookings',
                                            mappingName: 'bookings',
                                        });
                                        exportToXlsx(listToExport, 'bookings');
                                        notifySuccess(t('bookings.notifications.exportSuccess'));
                                    } catch (err) {
                                        notifyError(err);
                                    }
                                    setGeneratingXlsx(false);
                                    setProgress(0);
                                }}
                                label={t('common.export')}
                            >
                                {generatingXlsx ? (
                                    <CircularProgress
                                        variant="determinate"
                                        color="inherit"
                                        size={25}
                                        value={progress}
                                    />
                                ) : (
                                    <FileDownloadIcon />
                                )}
                            </Button>
                        </Tooltip>
                    </ButtonGroup>
                </Grid2>,
            ]}
        >
            <BookingsFilter
                filters={filters}
                setFilters={setFilters}
                setParams={setParams}
                limit={params.pageSize}
                total={bookingsData?.total}
                totalRevenue={bookingsData?.totalRevenue}
                totalPersons={bookingsData?.totalPersons}
                revenueBookingCount={bookingsData?.revenueBookingCount}
                summaryEstimated={bookingsData?.summaryEstimated}
                loading={loading}
            />
            <BookingsTable
                rows={bookingsData?.bookings}
                limit={params.pageSize}
                page={params.page}
                setParams={setParams}
                total={bookingsData?.total}
                loading={loading}
                sort={params.sort}
                sortDir={params.sortDir}
                setSorting={handleSorting}
                filters={filters}
                refreshBookings={refetch}
                clearFilters={clearFilters}
            />
        </PageWithTitle>
    );
};

export default BookingsPage;
